import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ExternalLink, File, Download, X, Video } from "lucide-react";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { isVideoMediaFile, isImageMediaFile } from "@/components/attachments/inline-media";

type Attachment = {
  id: string;
  name: string;
  type: "media" | "drive" | "url";
  fileUrl?: string;
  fileType?: string;
  webViewLink?: string;
  url?: string;
};

export function isVideoAttachment(att: Attachment): boolean {
  return att.type === "media" && isVideoMediaFile(att);
}

export function isImageAttachment(att: Attachment): boolean {
  return att.type === "media" && isImageMediaFile(att);
}

export function getEmbedUrl(url: string | undefined): string | null {
  if (!url) return null;
  try {
    if (url.includes('youtube.com/watch') || url.includes('youtu.be/')) {
      const videoId = url.includes('youtu.be/') 
        ? url.split('youtu.be/')[1]?.split('?')[0] 
        : new URLSearchParams(url.split('?')[1]).get('v');
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
    }
    if (url.includes('loom.com/share/')) {
      const videoId = url.split('loom.com/share/')[1]?.split('?')[0];
      return videoId ? `https://www.loom.com/embed/${videoId}` : null;
    }
  } catch (e) {
    return null;
  }
  return null;
}

export function SopAttachmentsList({ sopId, readOnly = false }: { sopId: string, readOnly?: boolean }) {
  const { toast } = useToast();

  const { data: attachments = [], isLoading } = useQuery<Attachment[]>({
    queryKey: [`/api/sops/${sopId}/attachments`],
    queryFn: async () => {
      const res = await fetch(`/api/sops/${sopId}/attachments`);
      if (!res.ok) throw new Error("Failed to fetch attachments");
      const data = await res.json();
      
      const all: Attachment[] = [];
      if (data.media) {
        data.media.forEach((m: any) => all.push({ id: m.id, name: m.name, type: "media", fileUrl: m.fileUrl, fileType: m.fileType }));
      }
      if (data.drive) {
        data.drive.forEach((m: any) => all.push({ id: m.id, name: m.name, type: "drive", webViewLink: m.webViewLink }));
      }
      if (data.urls) {
        data.urls.forEach((m: any) => all.push({ id: m.id, name: m.name, type: "url", url: m.url }));
      }
      return all;
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ id, type }: { id: string; type: "media" | "drive" | "url" }) => {
      const endpoint = `/api/sops/${sopId}/${type}/${id}`;
      const res = await fetch(endpoint, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      toast({ title: "Attachment removed" });
      queryClient.invalidateQueries({ queryKey: [`/api/sops/${sopId}/attachments`] });
    },
    onError: () => {
      toast({ title: "Failed to remove attachment", variant: "destructive" });
    },
  });

  if (isLoading) {
    return <div className="text-sm text-muted-foreground animate-pulse">Loading attachments...</div>;
  }

  if (attachments.length === 0) {
    return <div className="text-sm text-muted-foreground p-4 border rounded-lg border-dashed text-center">No attachments added.</div>;
  }

  return (
    <div className="space-y-4">
      {attachments.map((att) => {
        const embedUrl = att.type === "url" ? getEmbedUrl(att.url) : null;
        const inlineVideo = isVideoAttachment(att);
        const inlineImage = !inlineVideo && isImageAttachment(att);

        return (
          <Card key={`${att.type}-${att.id}`} className="p-0 overflow-hidden">
            {inlineVideo || inlineImage ? (
              <div className="flex flex-col">
                <div className="flex items-center justify-between p-3 border-b bg-muted/30">
                  <div className="flex items-center gap-2">
                    {inlineVideo ? (
                      <Video className="w-4 h-4 text-blue-500" />
                    ) : (
                      <File className="w-4 h-4 text-orange-500" />
                    )}
                    <span className="font-medium text-sm">{att.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                      <a href={att.fileUrl} target="_blank" rel="noopener noreferrer" download={att.name}>
                        <Download className="h-4 w-4" />
                      </a>
                    </Button>
                    {!readOnly && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => deleteMutation.mutate({ id: att.id, type: att.type })}
                        data-testid={`button-delete-attachment-${att.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {inlineVideo ? (
                  <div className="aspect-video w-full bg-black">
                    <video
                      src={att.fileUrl}
                      controls
                      preload="metadata"
                      className="w-full h-full"
                      data-testid={`video-attachment-${att.id}`}
                    />
                  </div>
                ) : (
                  <div className="w-full bg-muted/20 flex justify-center">
                    <img
                      src={att.fileUrl}
                      alt={att.name}
                      className="max-h-96 w-auto max-w-full object-contain"
                      data-testid={`image-attachment-${att.id}`}
                    />
                  </div>
                )}
              </div>
            ) : embedUrl ? (
              <div className="flex flex-col">
                <div className="flex items-center justify-between p-3 border-b bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Video className="w-4 h-4 text-blue-500" />
                    <span className="font-medium text-sm">{att.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                      <a href={att.url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                    {!readOnly && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteMutation.mutate({ id: att.id, type: att.type })}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="aspect-video w-full bg-black">
                  <iframe src={embedUrl} className="w-full h-full border-0" allowFullScreen />
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3">
                {att.type === "url" ? (
                  <ExternalLink className="h-5 w-5 text-blue-500 flex-shrink-0" />
                ) : att.type === "drive" ? (
                  <File className="h-5 w-5 text-green-500 flex-shrink-0" />
                ) : (
                  <File className="h-5 w-5 text-orange-500 flex-shrink-0" />
                )}
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{att.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{att.type} link</p>
                </div>
                
                <div className="flex items-center gap-1 shrink-0">
                  {att.type === "media" && att.fileUrl && (
                    <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                      <a href={att.fileUrl} target="_blank" rel="noopener noreferrer" download={att.name}>
                        <Download className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                  {att.type === "drive" && att.webViewLink && (
                    <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                      <a href={att.webViewLink} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                  {att.type === "url" && att.url && (
                    <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                      <a href={att.url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => deleteMutation.mutate({ id: att.id, type: att.type })}
                      data-testid={`button-delete-attachment-${att.id}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
